/**
 * 简讯开放接口工具
 * 规则：列表与详情共用同一 latest 接口；详情按 id 回查匹配项。
 */

export const NEWS_API_URL =
  'https://oa.yunplant.com/api/openapi/news/brief/latest'

/** 富文本内图片为 /api/profile/... 相对路径，只需补全接口所在域名 */
export const NEWS_ASSET_BASE_URL = new URL(NEWS_API_URL).origin

/** 列表摘要最大字数（中英文均按字符计数） */
export const NEWS_BRIEF_MAX_CHARS = 200

/**
 * 根据 URL ?lang= 映射接口语言参数
 * @returns {'zh-CN' | 'en'}
 */
export function getNewsApiLangByUrl() {
  const urlParams = new URLSearchParams(window.location.search)
  const rawLang = (urlParams.get('lang') || '').trim().toLowerCase()

  if (rawLang === 'zh' || rawLang === 'zh-cn' || rawLang === 'zn') {
    return 'zh-CN'
  }

  return 'en'
}

/** 去除 HTML 标签，保留纯文本（块级标签转为空格，避免段落粘连） */
export function stripHtmlTags(content = '') {
  if (typeof content !== 'string') return ''
  return content
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|h[1-6]|li|div|tr|blockquote)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, '&')
    .replace(/[\s\u3000]+/g, ' ')
    .trim()
}

/**
 * 把接口返回的相对资源路径补全为绝对地址
 * @param {string} src
 */
export function resolveAssetUrl(src = '') {
  const value = String(src).trim()
  if (!value) return ''
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value
  return `${NEWS_ASSET_BASE_URL}${value.startsWith('/') ? '' : '/'}${value}`
}

/**
 * 清洗 CMS 富文本，解决三类排版问题：
 * 1. 编辑器产生的空段落造成大段留白
 * 2. 段落开头的全角/半角缩进造成左边缘参差
 * 3. 图片为相对路径导致无法加载
 * @param {string} html
 */
export function normalizeContentHtml(html = '') {
  if (typeof html !== 'string' || !html.trim()) return ''

  return html
    .replace(
      /<(p|div)[^>]*>(?:\s|&nbsp;|\u3000|<br\s*\/?>)*<\/\1>/gi,
      ''
    )
    .replace(/(<(?:p|h[1-6]|li|td)[^>]*>)(?:\s|&nbsp;|\u3000)+/gi, '$1')
    .replace(/(?:\s|&nbsp;|\u3000)+(<\/(?:p|h[1-6]|li|td)>)/gi, '$1')
    .replace(
      /(<(?:p|h[1-6]|li|td)[^>]*>(?:<(?:strong|em|u|b|i|span)[^>]*>)+)(?:\s|&nbsp;|\u3000)+/gi,
      '$1'
    )
    .replace(
      /(?:\s|&nbsp;|\u3000)+((?:<\/(?:strong|em|u|b|i|span)>)+<\/(?:p|h[1-6]|li|td)>)/gi,
      '$1'
    )
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>')
    .replace(/<img\b([^>]*)>/gi, (match, attrs) => {
      const withAbsoluteSrc = attrs.replace(
        /src=(["'])(.*?)\1/i,
        (srcMatch, quote, src) => `src=${quote}${resolveAssetUrl(src)}${quote}`
      )
      const lazyAttrs = /loading=/i.test(withAbsoluteSrc)
        ? ''
        : ' loading="lazy" decoding="async"'
      return `<img${withAbsoluteSrc}${lazyAttrs}>`
    })
    .trim()
}

/**
 * 把接口时间格式化为阅读友好的日期
 * @param {string} raw 形如 2026-08-14 12:20:53
 * @param {'zh' | 'en'} lang
 */
export function formatNewsDate(raw = '', lang = 'en') {
  const value = String(raw).trim()
  if (!value) return ''

  // Safari 不接受 "2026-08-14 12:20:53"，统一替换分隔符
  const date = new Date(value.replace(/-/g, '/'))
  if (Number.isNaN(date.getTime())) return value

  if (lang === 'zh') {
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

/** 转义文本，避免标题等字段注入 HTML */
export function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * 截断纯文本；超出时追加省略号
 * @param {string} content
 * @param {number} maxChars
 */
export function truncateText(content = '', maxChars = NEWS_BRIEF_MAX_CHARS) {
  const text = stripHtmlTags(content)
  if (text.length <= maxChars) return text
  return `${text.slice(0, maxChars)}…`
}

/**
 * 解析简讯稳定 id（优先接口字段，否则用标题哈希 + 下标兜底）
 * @param {object} item
 * @param {number} index
 */
export function resolveNewsId(item = {}, index = 0) {
  const raw =
    item.id ?? item.newsId ?? item.briefId ?? item.uuid ?? item.news_id ?? null

  if (raw !== null && raw !== undefined && String(raw).trim() !== '') {
    return String(raw).trim()
  }

  const title = String(item.newsTitle || item.title || '').trim()
  if (title) {
    return `t-${simpleHash(title)}-${index}`
  }

  return `idx-${index}`
}

/** 简易稳定哈希，用于无 id 时生成可复现的详情链接 */
function simpleHash(input = '') {
  let hash = 0
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(36)
}

/**
 * 从接口字段或正文 HTML 中提取封面图
 * 优先级：newsHero（接口头图字段）> 其他常见封面字段 > 正文第一张图
 * newsHero 由接口返回完整地址，原样使用不做补全
 * @param {object} item
 * @param {string} htmlContent
 */
export function extractCoverImage(item = {}, htmlContent = '') {
  const hero = typeof item.newsHero === 'string' ? item.newsHero.trim() : ''
  if (hero) return hero

  const direct =
    item.newsImage ||
    item.coverImage ||
    item.cover ||
    item.image ||
    item.imageUrl ||
    item.thumbnail ||
    item.picUrl ||
    ''

  if (typeof direct === 'string' && direct.trim()) {
    return resolveAssetUrl(direct)
  }

  const match = String(htmlContent).match(
    /<img[^>]+src=["']([^"']+)["'][^>]*>/i
  )
  return match ? resolveAssetUrl(match[1]) : ''
}

/**
 * 将接口原始项归一化为列表/详情可用结构
 * @param {object} item
 * @param {number} index
 */
export function normalizeNewsItem(item = {}, index = 0) {
  const title = String(item?.newsTitle || item?.title || '').trim()
  const htmlContent = normalizeContentHtml(
    String(item?.newsContent || item?.content || '')
  )
  const plainText = stripHtmlTags(htmlContent)
  const id = resolveNewsId(item, index)
  const cover = extractCoverImage(item, htmlContent)
  const date =
    item?.publishTime ||
    item?.createTime ||
    item?.newsDate ||
    item?.date ||
    item?.createdAt ||
    ''

  return {
    id,
    title,
    /** 列表摘要：最多 200 字纯文本 */
    desc: truncateText(plainText, NEWS_BRIEF_MAX_CHARS),
    /** 是否被截断（用于展示「阅读更多」提示） */
    truncated: plainText.length > NEWS_BRIEF_MAX_CHARS,
    /** 详情页完整 HTML 正文（含图片） */
    contentHtml: htmlContent,
    cover,
    date: typeof date === 'string' ? date : String(date || '')
  }
}

/**
 * 拉取最新简讯列表并归一化
 * @returns {Promise<ReturnType<typeof normalizeNewsItem>[]>}
 */
export async function fetchLatestNewsItems() {
  const apiLang = getNewsApiLangByUrl()
  const url = `${NEWS_API_URL}?lang=${encodeURIComponent(apiLang)}`
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`News request failed with status ${response.status}`)
  }

  const result = await response.json()
  if (!Array.isArray(result?.data)) {
    return []
  }

  return result.data.map((item, index) => normalizeNewsItem(item, index))
}

/**
 * 按 id 查找单条简讯
 * @param {string} id
 */
export async function fetchNewsItemById(id) {
  const targetId = String(id || '').trim()
  if (!targetId) return null

  const items = await fetchLatestNewsItems()
  return items.find((item) => item.id === targetId) || null
}

/**
 * 取指定简讯及其在列表中的相邻两条，用于详情页翻页导航
 * 列表按时间倒序，prev 为更新的一条，next 为更早的一条
 * @param {string} id
 * @returns {Promise<{ item: object | null, prev: object | null, next: object | null }>}
 */
export async function fetchNewsContextById(id) {
  const empty = { item: null, prev: null, next: null }
  const targetId = String(id || '').trim()
  if (!targetId) return empty

  const items = await fetchLatestNewsItems()
  const index = items.findIndex((item) => item.id === targetId)
  if (index === -1) return empty

  return {
    item: items[index],
    prev: items[index - 1] || null,
    next: items[index + 1] || null
  }
}
